/* eslint-disable react-hooks/exhaustive-deps */
import { ProductApiConfig } from "api/configs/productApiConfig";
import Button from "components/ui/Button";
import Table, { TableColumns } from "components/ui/Table";
import Tabs from "components/ui/Tabs";
import Title from "components/ui/Title";
import { APPROVER_PAGE_SIZE, approverTabs } from "constants/productConstants";
import {
  ApprovedProductColumns,
  ApproverDraftProductColumns,
  ApproverPendingProductColumns,
} from "constants/TableColumns/productColumns";
import { useApp } from "context/appContext";
import { useApiQuery } from "hooks/useApi";
import { useState } from "react";
import { DatePicker, Pagination, Input } from "antd";
import moment from "moment";
import { DATE_FORMAT } from "constants/commonConstants";
import Spinner from "components/ui/Spinner";
import { disabledDate, downloadFile, handleError } from "utils/helper";

const { Search } = Input;

export default function ProductApprover() {
  const { RangePicker } = DatePicker;
  const [selectedTab, setSelectedTab] = useState("PENDING");
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const handleSearch = (value: string) => {
    setSearchQuery(value.trim());
    setPage(0);
  };

  const columnsByStatus: Record<string, TableColumns[]> = {
    DRAFT: ApproverDraftProductColumns,
    PENDING: ApproverPendingProductColumns,
    APPROVED: ApprovedProductColumns,
  };
  const { tenant } = useApp();
  const { data: response, isLoading: getLoader } = useApiQuery(
    ProductApiConfig.GET_APPROVER_PRODUCTS_BY_STATUS,
    {
      pathParams: { status: selectedTab, tenant },
      params: {
        page,
        size: APPROVER_PAGE_SIZE,
        ...(searchQuery && { searchQuery }),
      },
    }
  );
  const products = response?.data ?? [];
  const pagination = response?.pagination;

  const handleTabChange = (tab: string) => {
    setSelectedTab(tab);
    setPage(0);
    setSearchInput("");
    setSearchQuery("");
  };

  const [fromDate, setFromDate] = useState<any>();
  const [toDate, setToDate] = useState<any>();
  const { refetch: download, isLoading } = useApiQuery(
    ProductApiConfig.DOWNLOAD_PRODUCTS_BY_STATUS,
    {
      pathParams: {
        status: selectedTab,
        fromDate: moment(fromDate).format(DATE_FORMAT),
        toDate: moment(toDate).format(DATE_FORMAT),
      },
      successCb: (data) =>
        downloadFile(
          data,
          `${selectedTab}-PRODUCTS_FROM_${moment(fromDate).format(
            DATE_FORMAT
          )}_TO_${moment(toDate).format(DATE_FORMAT)}.xlsx`
        ),
      errorCb: handleError,
    }
  );

  return (
    <div className="p-20">
      <Title title="Product Approver's Page" />
      <div className="p-20">
        <Tabs
          list={approverTabs}
          selectedTab={selectedTab}
          onClick={handleTabChange}
        />
        <div className="flex gap-x-20 py-20">
          <RangePicker
            onChange={([from, to]) => {
              setFromDate(from);
              setToDate(to);
            }}
            disabledDate={disabledDate}
            value={[fromDate, toDate]}
          />
          <Button
            text={"Download"}
            onButtonClick={() => download()}
            btnClass={"bg-btn"}
            loading={isLoading}
            loadingText="Downloading..."
            disabled={isLoading || !fromDate || !toDate}
          />
          <Search
            placeholder="Search by ASN or Model Name"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onSearch={handleSearch}
            allowClear
            enterButton="Search"
            className="ml-auto max-w-[300px]"
          />
        </div>
        <div className="py-20">
          {getLoader ? (
            <Spinner />
          ) : products?.length ? (
            <>
              <Table
                columns={columnsByStatus[selectedTab]}
                data={products}
                loading={false}
                hasPagination={false}
                manualPagination
              />
              {pagination && (
                <div className="flex justify-end py-20">
                  <Pagination
                    current={page + 1}
                    pageSize={APPROVER_PAGE_SIZE}
                    total={pagination.totalItems}
                    onChange={(p) => setPage(p - 1)}
                    showSizeChanger={false}
                    showTotal={(total, range) =>
                      `${range[0]}-${range[1]} of ${total} items`
                    }
                  />
                </div>
              )}
            </>
          ) : (
            <p className="text-20 font-bold text-center py-20">No data found</p>
          )}
        </div>
      </div>
    </div>
  );
}
